import React from 'react'
import {Box, Card, Chip, Stack, Tooltip, Typography} from '@mui/material'
import VideocamIcon from '@mui/icons-material/Videocam'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import TodayIcon from '@mui/icons-material/Today'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt'
import DateRangeIcon from '@mui/icons-material/DateRange'
import moment from 'moment'
import styles from '@containers/monitoringPlatform/geoZones/list/sx'
import {IAccountCameraRecording, IDeviceCameraRecording} from '@containers/monitoringPlatform/geoZones/types'
import {CAMERA_POSITION_LABELS} from '@containers/cameras/select/selectCameraPosition/constants'
import ModalCustom from '@ui/modal/ModalCustom'
import ButtonCustom from '@ui/buttons/buttonCustom/ButtonCustom'
import {DeviceRecordingsByPeriod} from '@containers/monitoringPlatform/geoZones/recordingsByPeriod/DeviceRecordingsByPeriod'
import LinkToSnapshotsByTime from '@containers/cameras/links/linkToSnapshotsByTime/LinkToSnapshotsByTime'

const renderLastRecording = (lastRecordingAt: string | null) => {
	if (!lastRecordingAt) {
		return (
			<Typography variant="caption" fontWeight={700} color="text.disabled">
				ще не було записів
			</Typography>
		)
	}
	return (
		<Tooltip title={moment(lastRecordingAt).format('DD.MM.YYYY HH:mm')}>
			<Typography variant="caption" fontWeight={700} color="text.secondary">
				{moment(lastRecordingAt).fromNow()}
			</Typography>
		</Tooltip>
	)
}

const CameraRow = ({camera, singleVoyage}: {camera: IDeviceCameraRecording, singleVoyage?: boolean}) => {
	const position = camera.cameraPosition
		? (CAMERA_POSITION_LABELS[camera.cameraPosition] ?? camera.cameraPosition)
		: null

	return (
		<Box sx={{
			p: 1,
			borderRadius: 1,
			border: '1px solid',
			borderColor: 'divider',
			mb: 0.5,
		}}>
			<Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
				<Stack direction="row" alignItems="center" spacing={0.5}>
					<VideocamIcon sx={{fontSize: 14, color: 'text.secondary'}}/>
					<Typography variant="caption" fontWeight={700} noWrap>
						{camera.cameraId}
					</Typography>
					{camera.cameraCounter != null && (
						<Typography variant="caption" color="text.disabled">
							· ch{camera.cameraCounter}
						</Typography>
					)}
				</Stack>
				{position && (
					<Chip
						label={position}
						size="small"
						variant="outlined"
						sx={{height: 18, fontSize: 10}}
					/>
				)}
			</Stack>

			<Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
				<Stack direction="row" alignItems="center" spacing={0.3}>
					<TodayIcon sx={{fontSize: 12, color: 'primary.main'}}/>
					<Typography variant="caption" color="text.secondary">сьогодні:</Typography>
					<Typography variant="caption" fontWeight={700}>{camera.todayRecordings}</Typography>
				</Stack>
				<Stack direction="row" alignItems="center" spacing={0.3}>
					<VideoLibraryIcon sx={{fontSize: 12, color: 'text.secondary'}}/>
					<Typography variant="caption" color="text.secondary">всього:</Typography>
					<Typography variant="caption" fontWeight={700}>
						{camera.totalRecordings.toLocaleString()}
					</Typography>
				</Stack>
				{!singleVoyage && (
					<Tooltip title="Унікальних заїздів у геозони сьогодні">
						<Stack direction="row" alignItems="center" spacing={0.3}>
							<AddLocationAltIcon sx={{fontSize: 12, color: 'success.main'}}/>
							<Typography variant="caption" color="text.secondary">геозони:</Typography>
							<Typography variant="caption" fontWeight={700}>{camera.todayGeozoneEntries}</Typography>
						</Stack>
					</Tooltip>
				)}
				<Stack direction="row" alignItems="center" spacing={0.3}>
					<AccessTimeIcon sx={{fontSize: 12, color: 'text.secondary'}}/>
					{renderLastRecording(camera.lastRecordingAt)}
				</Stack>
			</Stack>
		</Box>
	)
}

interface IProps {
	recording: IAccountCameraRecording
	accountId: number
	singleVoyage?: boolean
}

export const RecordingsCard = ({recording, accountId, singleVoyage}: IProps) => {
	const totalRecordings = recording.cameras.reduce((sum, c) => sum + (c.totalRecordings || 0), 0)
	const todayRecordings = recording.cameras.reduce((sum, c) => sum + (c.todayRecordings || 0), 0)
	const todayGeozoneEntries = recording.cameras.reduce((sum, c) => sum + (c.todayGeozoneEntries || 0), 0)

	return (
		<Card variant="outlined" sx={styles.card}>
			<Box sx={styles.cardHeader}>
				<Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5}}>
					<Stack direction="row" alignItems="center" spacing={1}>
						<Box sx={styles.statusDot(recording.isActive)}/>
						<Typography variant="subtitle1" sx={{fontWeight: 700, lineHeight: 1.2}}>
							{recording.licencePlate || '—'}
						</Typography>
					</Stack>
					<Typography variant="caption" color="text.secondary">
						ID {recording.deviceId}
					</Typography>
				</Box>

				<Stack direction="row" alignItems="center" spacing={1}>
					<Chip
						label={recording.cameraType}
						size="small"
						sx={styles.brandChip(recording.cameraType)}
					/>
					<Typography variant="caption" color="text.secondary">
						{recording.isActive ? 'В мережі' : 'Активності немає'}
					</Typography>
					<Typography variant="caption" color="text.disabled" sx={{ml: 'auto'}}>
						{recording.cameras.length} кам.
					</Typography>
				</Stack>

				{recording.hostName && recording.hostName !== 'null' && (
					<Typography variant="caption" color="text.disabled" display="block" mt={0.5} noWrap>
						{recording.hostName}
					</Typography>
				)}
			</Box>

			<Box sx={{p: 2}}>
				<Stack direction="row" spacing={2} mb={1.5}>
					<Stack direction="row" alignItems="center" spacing={0.5}>
						<TodayIcon sx={{fontSize: 14, color: 'primary.main'}}/>
						<Typography variant="caption" color="text.secondary">
							Сьогодні:
						</Typography>
						<Typography variant="caption" fontWeight={700}>
							{todayRecordings.toLocaleString()}
						</Typography>
					</Stack>
					<Stack direction="row" alignItems="center" spacing={0.5}>
						<VideoLibraryIcon sx={{fontSize: 14, color: 'text.secondary'}}/>
						<Typography variant="caption" color="text.secondary">
							Всього:
						</Typography>
						<Typography variant="caption" fontWeight={700}>
							{totalRecordings.toLocaleString()}
						</Typography>
					</Stack>
					{!singleVoyage && (
						<Tooltip title="Унікальних заїздів у геозони сьогодні">
							<Stack direction="row" alignItems="center" spacing={0.5}>
								<AddLocationAltIcon sx={{fontSize: 14, color: 'success.main'}}/>
								<Typography variant="caption" color="text.secondary">
									Геозони:
								</Typography>
								<Typography variant="caption" fontWeight={700}>
									{todayGeozoneEntries.toLocaleString()}
								</Typography>
							</Stack>
						</Tooltip>
					)}
				</Stack>

				<Box>
					{recording.cameras.map((camera) => (
						<CameraRow key={camera.cameraId} camera={camera} singleVoyage={singleVoyage}/>
					))}
				</Box>

				<Box sx={{mt: 1.5}}>
					<ModalCustom
						label={`Записи за період · ${recording.licencePlate || recording.deviceId}`}
						button={
							<ButtonCustom
								startIcon={<DateRangeIcon/>}
								variant="outlined"
								fullWidth
							>
								Записи за період
							</ButtonCustom>
						}
						component={
							<DeviceRecordingsByPeriod
								accountId={accountId}
								deviceId={recording.deviceId}
								singleVoyage={singleVoyage}
							/>
						}
					/>
				</Box>

				{singleVoyage && (
					<Box sx={{mt: 1}}>
						<LinkToSnapshotsByTime
							accountId={accountId}
							deviceId={recording.deviceId}
						/>
					</Box>
				)}
			</Box>
		</Card>
	)
}
